import React, { useCallback, useEffect, useState } from "react";
import { Col, Container } from "reactstrap";
import { assignBulkTaskApi, retrieveAllTasks, retrieveCurrentRoutine, startTask } from "utils/api";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import { Spin, Tooltip } from "antd";
import { makeStyles } from "@material-ui/core/styles";
import classnames from "classnames";
import resumeIcon from "assets/images/svg/restartIcon.svg";
import startIcon from "assets/images/svg/playIcon.svg";
import scheduleIcon from "assets/images/svg/scheduleIcon.svg";
import { AUTH_USER } from "utils/constants";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import SetAvailabilityModal from "pages/customer-management/customerDetails/SetAvaliabilityModal";
import NGIEmpty from "components/Common/components/NGIEmpty";
import ExecuteTaskModal from "pages/operations/interventionCall/ExecuteTaskModal";
import CustomLink from "components/Common/buttons/CustomLink";
import infoBlue from "assets/images/svg/InfoBlue.svg";
import { useLocation } from "react-router";
import BackButtonLink from "components/Common/buttons/BackButtonLink";
import NGITooltip from "components/Common/utils/NGITooltip";
import { getDictionary } from "store/dictionaries/actions";
import { DICTIONARY_TYPES } from "store/dictionaries/constants";
import { useDispatch, useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";

const useStyles = makeStyles({
  itemContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    padding: 15,
    borderRadius: 5,
    border: "1px solid rgba(0, 0, 0, 0.10)",
    background: "#FFF",
    marginBottom: 10,
  },
  item: {
    fontWeight: 600,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    paddingRight: 5,

    "& span": {
      color: "#8C8C8C",
      fontSize: 14,
      fontWeight: 300,
      marginRight: 5,
    },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "5px",
  },
  button: {
    padding: "6px 12px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "3px",
    borderRadius: 2,
    fontWeight: 600,
    fontSize: 9,

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  resume: {
    backgroundColor: "#0085FF26",
    color: "#0085FF",
  },
  start: {
    backgroundColor: "#D8FFE3",
    color: "#17C347",
  },
  schedule: {
    backgroundColor: "#DB660726",
    color: "#DB6607",
  },
  informationIcon: {
    marginRight: 10,
    cursor: "pointer",
  },
  backButtonContainer: {
    display: "flex",
    marginBottom: 15,
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: "none",
  },
});

const ALLOWED_STATUSES = ["In progress", "Assigned"];
const InterventionCall = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isLoading, setLoader] = useState(false);
  const [tasksList, setTasksList] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [isSetAvaliabilityOpen, setIsSetAvaliabilityOpen] = useState(false);
  const [isExecuteTaskOpen, setIsExecuteTaskOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [hasTaskInProgress, setHasTaskInProgress] = useState(true);
  const searchParams = useLocation().search;
  const backButtonUrl = new URLSearchParams(searchParams).get("backUrl") || null;

  const {
    list: { interventionCallSurvey },
  } = useSelector(DictionariesSelector);

  useEffect(() => {
    handleSearch();
    retrieveCurrenUserRoutine();

    dispatch(getDictionary(DICTIONARY_TYPES.INTERVENTION_CALL_SURVEY));
  }, []);

  const openAssignModal = useCallback(() => setAssignOpen(true), []);
  const closeAssignModal = useCallback(() => setAssignOpen(false), []);

  const openStartModal = useCallback(() => setStartOpen(true), []);
  const closeStartModal = useCallback(() => setStartOpen(false), []);

  const onSetAvailability = useCallback(() => setIsSetAvaliabilityOpen(true), []);
  const onSetAvailabilityClose = useCallback(() => setIsSetAvaliabilityOpen(false), []);

  const onExecuteTaskOpen = useCallback(() => setIsExecuteTaskOpen(true), []);
  const onExecuteTaskClose = useCallback(() => setIsExecuteTaskOpen(false), []);

  const handleSearch = () => {
    setLoader(true);

    retrieveAllTasks({
      category: "Intervention Call",
      depot: AUTH_USER()?.depotName,
      pageSize: 1000,
    })
      .then(res => {
        const {
          result: { items },
        } = res?.data;
        if (items?.length === 0) {
          warnMessage("Tasks were not found");
        }
        const sorted = res?.data?.result?.items
          ?.filter(item => ALLOWED_STATUSES.includes(item.status))
          ?.sort((a, b) => a.customer.name.localeCompare(b.customer.name));

        setTasksList(sorted || []);
        setLoader(false);
      })
      .catch(err => {
        setLoader(false);
        setTasksList(null);
        defaultNgiErrorMethod(err);
      });
  };

  const retrieveCurrenUserRoutine = useCallback(() => {
    const { email } = AUTH_USER();
    retrieveCurrentRoutine(email)
      .then(res => {
        const { result } = res?.data;
        const inProgressTask = result?.tasks?.find(item => item.status === "In progress");
        setHasTaskInProgress(inProgressTask);
      })
      .catch(err => {
        setHasTaskInProgress(false);
        console.log(err);
      });
  }, []);

  const onAssign = () => {
    setLoader(true);
    const { email, depotName, firstName, lastName } = AUTH_USER();

    const options = {
      assignTo: email,
      assignToName: `${firstName} ${lastName}`,
      assignToDepot: depotName,
      taskIds: [activeItem.id],
    };
    closeAssignModal();
    assignBulkTaskApi(options)
      .then(() => {
        onStartTask(true);
      })
      .catch(error => {
        setLoader(false);
        defaultNgiErrorMethod(error);
      });
  };

  const onStartTask = (isSelfAssigned = false) => {
    setLoader(true);
    startTask(activeItem.id)
      .then(() => {
        setTimeout(() => {
          successMessage("Task was started successfully");
          handleSearch();
          setLoader(false);
          if (activeItem?.assignedTo?.toLowerCase() === AUTH_USER()?.email?.toLowerCase() || isSelfAssigned) {
            onExecuteTaskOpen();
          }
        }, 2000);
      })
      .catch(e => {
        setLoader(false);
        defaultNgiErrorMethod(e);
      });
  };

  const handleStart = useCallback(
    event => {
      if (hasTaskInProgress) {
        warnMessage("You have already started a task. Please complete or delay it firstly.");
        return;
      }
      const id = event.currentTarget.dataset.id;
      const foundItem = tasksList.find(item => item.id === id);
      setActiveItem(foundItem);
      const { assignedTo } = foundItem;
      if (!assignedTo || assignedTo?.toLowerCase() !== AUTH_USER()?.email?.toLowerCase()) {
        openAssignModal();
        return;
      }
      openStartModal();
    },
    [tasksList, hasTaskInProgress]
  );

  const handleSchedule = useCallback(
    event => {
      const id = event.currentTarget.dataset.id;
      const foundItem = tasksList.find(item => item.id === id);
      setActiveItem(foundItem);
      onSetAvailability();
    },
    [tasksList]
  );

  const handleExecute = useCallback(
    event => {
      const id = event.currentTarget.dataset.id;
      const foundItem = tasksList.find(item => item.id === id);
      setActiveItem(foundItem);
      onExecuteTaskOpen();
    },
    [tasksList]
  );

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Spin spinning={isLoading}>
            {backButtonUrl && (
              <div className={classes.backButtonContainer}>
                <BackButtonLink to={backButtonUrl}>Back</BackButtonLink>
              </div>
            )}

            <Col lg={12}>
              {tasksList?.length ? (
                tasksList?.map(item => (
                  <div className={classes.itemContainer} key={item.id}>
                    <div className={classes.item}>
                      <Tooltip placement="bottom" title="Open Task Details" color="#444444">
                        <img
                          onClick={() => {
                            window.open(
                              `/operations/task/${item.id}?backUrl=/operations/tasks/intervention-call`,
                              "_blank"
                            );
                          }}
                          className={classes.informationIcon}
                          src={infoBlue}
                          alt="info"
                        />
                      </Tooltip>

                      <CustomLink
                        title={item.customer.name}
                        underlined
                        target="_blank"
                        to={`/customers/customer/${item.customer.id}`}
                      >
                        {item.customer.name}
                      </CustomLink>
                    </div>
                    <div className={classes.item} title={item.customer.id}>
                      <span>Account ID:</span>
                      <CustomLink underlined target="_blank" to={`/customers/customer/${item.customer.id}`}>
                        {item.customer.id}
                      </CustomLink>
                    </div>
                    <div className={classes.item} title={item.customer.phone}>
                      <span>Phone:</span>
                      {item.customer.phone}
                    </div>
                    <div className={classes.item} title={item.depot}>
                      <span>Depot:</span>
                      {item.depot}
                    </div>
                    <div className={classes.actions}>
                      {item.status === "In progress" && (
                        <NGITooltip
                          title={
                            item?.assignedTo?.toLowerCase() !== AUTH_USER()?.email?.toLowerCase()
                              ? "The assigned user is different. Please reassign or log in with the assigned user to proceed with the task."
                              : "Resume Task"
                          }
                        >
                          <div
                            onClick={handleExecute}
                            data-id={item.id}
                            className={classnames(
                              classes.button,
                              classes.resume,
                              item?.assignedTo?.toLowerCase() !== AUTH_USER()?.email?.toLowerCase() && classes.disabled
                            )}
                          >
                            <img src={resumeIcon} alt="resumeIcon" />
                            RESUME TASK
                          </div>
                        </NGITooltip>
                      )}
                      {item.status === "Assigned" && (
                        <>
                          <div
                            onClick={handleSchedule}
                            data-id={item.id}
                            className={classnames(classes.button, classes.schedule)}
                          >
                            <img src={scheduleIcon} alt="scheduleIcon" />
                            SCHEDULE
                          </div>
                          <div
                            onClick={handleStart}
                            data-id={item.id}
                            className={classnames(classes.button, classes.start)}
                          >
                            <img src={startIcon} alt="startIcon" />
                            START TASK
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <NGIEmpty />
              )}
            </Col>
          </Spin>
          {assignOpen && (
            <ConfirmationModal
              title="Assign to Continue"
              description="To start working on this task, you need to assign it to yourself. Do you want to assign it now?"
              onCancel={closeAssignModal}
              onConfirm={onAssign}
            />
          )}
          {startOpen && (
            <ConfirmationModal
              description="Are you sure you want to start task?"
              onCancel={closeStartModal}
              onConfirm={onStartTask}
            />
          )}
          {isSetAvaliabilityOpen && (
            <SetAvailabilityModal
              update={handleSearch}
              customerId={activeItem?.customer?.id}
              onClose={onSetAvailabilityClose}
            />
          )}
          {isExecuteTaskOpen && (
            <ExecuteTaskModal
              interventionCallSurvey={interventionCallSurvey}
              update={handleSearch}
              data={activeItem}
              onClose={onExecuteTaskClose}
            />
          )}
        </Container>
      </div>
    </>
  );
};

export default InterventionCall;
